<script>
	import { createEventDispatcher } from 'svelte';
	import { signOut } from '../lib/auth.js';
	import { RefreshCw, Settings, X, Menu } from 'lucide-svelte';

	export let navigate;
	export let active = '';  // 'explore' | 'query' | 'discover' | 'connect' | 'ops'
	export let guideFlash = false;

	const dispatch = createEventDispatcher();

	let mobileMenuOpen = false;

	function mobileNav(path) {
		mobileMenuOpen = false;
		navigate(path);
	}
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<nav class="navbar">
	<div class="nav-left">
		<span class="nav-brand" on:click={() => navigate('/')}>ADRASI</span>
	</div>
	<div class="nav-center">
		<div class="nav-pill-group">
			<span class="nav-link" class:active={active === 'explore'} on:click={() => { if (active !== 'explore') navigate('/explorer'); }}>Explore</span>
			<span class="nav-link" class:active={active === 'source'} on:click={() => { if (active !== 'source') navigate('/source'); }}>Source</span>
			<span class="nav-link" class:active={active === 'query'} on:click={() => { if (active !== 'query') navigate('/query'); }}>Query</span>
		</div>
		<div class="nav-pill-group">
			<span class="nav-link" class:active={active === 'connect'} on:click={() => { if (active !== 'connect') navigate('/connect'); }}>Connect</span>
			<span class="nav-link" class:active={active === 'gardener'} on:click={() => { if (active !== 'gardener') navigate('/gardener'); }}>Gardener</span>
			<span class="nav-link" class:active={active === 'ops'} on:click={() => { if (active !== 'ops') navigate('/ops'); }}>Log</span>
		</div>
	</div>
	<div class="nav-right">
		<slot name="actions"></slot>
		<!-- svelte-ignore a11y-click-events-have-key-events -->
		<span class="nav-refresh" on:click={() => dispatch('refresh')} title="Refresh">
			<RefreshCw size={16} strokeWidth={1.5} />
		</span>
		<span class="nav-link nav-guide" class:guide-flash={guideFlash} on:click={() => dispatch('guide')}>Guide</span>
		<span class="nav-link" class:active={active === 'admin'} on:click={() => navigate('/admin')}>
			<Settings size={16} strokeWidth={1.5} />
		</span>
		<span class="nav-sign-out" on:click={signOut}>Sign out</span>
	</div>
	<span class="hamburger" on:click={() => { mobileMenuOpen = !mobileMenuOpen; }}>
		{#if mobileMenuOpen}
			<X size={20} strokeWidth={1.5} />
		{:else}
			<Menu size={20} strokeWidth={1.5} />
		{/if}
	</span>
</nav>

<!-- Lock body scroll when the mobile menu is open.
     Prevents iOS/Safari double-scroll (menu overlay + body scrolling behind it). -->
<svelte:body class:body-scroll-locked={mobileMenuOpen} />

{#if mobileMenuOpen}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<div class="mobile-menu">
		<span class="mobile-link" class:active={active === 'explore'} on:click={() => mobileNav('/explorer')}>Explore</span>
		<span class="mobile-link" class:active={active === 'source'} on:click={() => mobileNav('/source')}>Source</span>
		<span class="mobile-link" class:active={active === 'query'} on:click={() => mobileNav('/query')}>Query</span>
		<span class="mobile-link" class:active={active === 'connect'} on:click={() => mobileNav('/connect')}>Connect</span>
		<span class="mobile-link" class:active={active === 'gardener'} on:click={() => mobileNav('/gardener')}>Gardener</span>
		<span class="mobile-link" class:active={active === 'ops'} on:click={() => mobileNav('/ops')}>Log</span>
		<div class="mobile-divider"></div>
		<span class="mobile-link" class:active={active === 'admin'} on:click={() => mobileNav('/admin')}>Settings</span>
		<span class="mobile-link" on:click={() => { mobileMenuOpen = false; dispatch('guide'); }}>Guide</span>
		<span class="mobile-link mobile-signout" on:click={signOut}>Sign out</span>
	</div>
{/if}

<style>
	.navbar {
		display: flex;
		align-items: center;
		height: 54px;
		padding: 0 20px;
		background: #111827;
		border-bottom: 1px solid #1F2937;
		flex-shrink: 0;
		position: sticky;
		top: 0;
		z-index: 10;
	}

	.nav-left { display: flex; align-items: center; }
	.nav-brand {
		font-size: 15px;
		font-weight: 800;
		color: #F9FAFB;
		letter-spacing: -0.3px;
		margin-right: 32px;
		cursor: pointer;
		transition: opacity 150ms ease-out;
	}
	.nav-brand:hover { opacity: 0.8; }

	.nav-center { display: flex; align-items: center; gap: 10px; }

	.nav-pill-group {
		display: flex;
		align-items: center;
		border: 1px solid #374151;
		border-radius: 8px;
		overflow: hidden;
	}
	.nav-pill-group .nav-link { border-radius: 0; }
	.nav-pill-group .nav-link + .nav-link { border-left: 1px solid #374151; }

	.nav-link {
		font-size: 13px;
		font-weight: 500;
		color: #9CA3AF;
		cursor: pointer;
		padding: 6px 14px;
		border-radius: 6px;
		transition: all 120ms ease-out;
	}
	.nav-link:hover { color: #E5E7EB; background: rgba(255,255,255,0.06); }
	.nav-link.active {
		color: #F9FAFB;
		background: #374151;
		font-weight: 600;
		cursor: default;
	}
	.nav-link.active:hover { background: #374151; }

	.nav-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }

	.nav-refresh {
		color: #6B7280;
		cursor: pointer;
		padding: 4px;
		border-radius: 4px;
		transition: all 150ms ease-out;
		display: flex;
		align-items: center;
	}
	.nav-refresh:hover { color: #E5E7EB; background: rgba(255,255,255,0.06); }
	.nav-refresh:active { transform: rotate(-45deg); }

	@keyframes guide-flash-anim {
		0%   { color: #F9FAFB; background: rgba(255,255,255,0.12); }
		70%  { color: #F9FAFB; background: rgba(255,255,255,0.08); }
		100% { color: #9CA3AF; background: transparent; }
	}
	.nav-guide.guide-flash { animation: guide-flash-anim 1.8s ease-out forwards; }

	.nav-sign-out {
		font-size: 12px;
		color: #6B7280;
		cursor: pointer;
		transition: color 150ms ease-out;
	}
	.nav-sign-out:hover { color: #D1D5DB; }

	/* Hamburger button — hidden on desktop */
	.hamburger {
		display: none;
		margin-left: auto;
		color: #9CA3AF;
		cursor: pointer;
		padding: 8px;
		transition: color 150ms ease-out;
	}
	.hamburger:hover { color: #E5E7EB; }

	/* Mobile menu dropdown */
	.mobile-menu {
		display: none;
		position: fixed;
		top: 54px;
		left: 0;
		right: 0;
		bottom: 0;
		background: #111827;
		z-index: 9;
		padding: 12px 20px;
		flex-direction: column;
		gap: 4px;
		overflow-y: auto;
	}

	.mobile-link {
		display: block;
		font-size: 15px;
		font-weight: 500;
		color: #9CA3AF;
		padding: 12px 16px;
		border-radius: 8px;
		cursor: pointer;
		transition: all 120ms ease-out;
	}
	.mobile-link:hover { color: #E5E7EB; background: rgba(255,255,255,0.06); }
	.mobile-link.active { color: #F9FAFB; background: #374151; font-weight: 600; }
	.mobile-signout { color: #6B7280; }

	.mobile-divider {
		height: 1px;
		background: #1F2937;
		margin: 8px 0;
	}

	@media (max-width: 768px) {
		.nav-center { display: none; }
		.nav-right { display: none; }
		.hamburger { display: flex; }
		.mobile-menu { display: flex; }
	}

	/* Global — applied to <body> when the mobile menu is open.
	   Prevents background scroll while the fullscreen overlay is visible. */
	:global(body.body-scroll-locked) {
		overflow: hidden;
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		width: 100%;
	}
</style>
